import type { RequestError } from "@/types/records";
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { clearAppLocalStorage, clearAppSessionStorage } from "@/lib/storage";
import { setSentryUser } from "@/lib/sentry";
import { setClarityUser } from "@/lib/clarity";

type AppRole = "admin" | "producer" | "buyer" | "staff";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    phone: string | null;
    cpf: string | null;
    document_number: string | null;
    document_type: "cpf" | "cnpj" | null;
    birth_date: string | null;
    cep: string | null;
    street: string | null;
    neighborhood: string | null;
    address_number: string | null;
    complement: string | null;
    city: string | null;
    state: string | null;
    producer_status: string | null;
    organizer_slug: string | null;
    organizer_bio: string | null;
    organizer_instagram: string | null;
    organizer_facebook: string | null;
    organizer_website: string | null;
    organizer_logo_url: string | null;
    organizer_banner_url: string | null;
  } | null;
  role: AppRole | null;
  allRoles: AppRole[];
  switchRole: (role: AppRole) => void;
  loading: boolean;
  signOut: () => Promise<void>;
  refetchRole: () => Promise<void>;
}

const ROLE_PRIORITY: AppRole[] = ["admin", "producer", "staff", "buyer"];

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  role: null,
  allRoles: [],
  switchRole: () => {},
  loading: true,
  signOut: async () => {},
  refetchRole: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [allRoles, setAllRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const sessionGeneration = useRef(0);
  const activeUserId = useRef<string | null>(null);

  const ensureProfile = async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.info("[auth] ensureProfile start: " + JSON.stringify({
        userId,
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        sessionUserId: session?.user?.id ?? null,
      }));
      if (!session?.access_token) {
        console.warn("ensure-user-profile skipped: no active auth session");
        return;
      }
      const result = await supabase.functions.invoke("ensure-user-profile", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      console.info("[auth] ensureProfile result: " + JSON.stringify({
        error: result.error?.message ?? null,
        data: result.data ?? null,
      }));
    } catch (e) {
      console.warn("[auth] ensure-user-profile failed (non-blocking):", e);
    }
  };


  const fetchProfile = async (userId: string, generation = sessionGeneration.current) => {
    // Tentativa 1: select completo de colunas existentes no esquema atual
    const result = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    let profileData: AuthContextType["profile"] | null = null;

    if (!result.error && result.data) {
      profileData = result.data as unknown as AuthContextType["profile"];
      console.info("[auth] fetchProfile success: " + JSON.stringify({
        userId,
        selectedFields: "all-existing",
        hasProfile: !!profileData,
      }));
    } else {
      console.warn("[auth] fetchProfile full select failed, trying fallback: " + JSON.stringify({
        userId,
        code: (result.error as RequestError)?.code ?? null,
        message: (result.error as RequestError)?.message ?? null,
        hint: (result.error as RequestError)?.hint ?? null,
        details: (result.error as RequestError)?.details ?? null,
      }));
      const fallback = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, phone, document_number, document_type")
        .eq("id", userId)
        .single();
      if (!fallback.error && fallback.data) {
        profileData = fallback.data as unknown as AuthContextType["profile"];
        console.info("[auth] fetchProfile fallback success", {
          userId,
          selectedFields: "safe",
          hasProfile: !!profileData,
        });
      } else {
        console.error("[auth] fetchProfile fallback also failed", {
          userId,
          code: (fallback.error as RequestError)?.code ?? null,
          message: (fallback.error as RequestError)?.message ?? null,
          hint: (fallback.error as RequestError)?.hint ?? null,
          details: (fallback.error as RequestError)?.details ?? null,
        });
      }
    }

    if (generation === sessionGeneration.current) setProfile(profileData);
  };


  const fetchRole = async (userId: string, generation = sessionGeneration.current) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    
    // Todos os usuários autenticados possuem a role de comprador (buyer) por padrão
    const roles = ["buyer" as AppRole];
    if (data && data.length > 0) {
      data.forEach((r) => {
        if (r.role && !roles.includes(r.role as AppRole)) {
          roles.push(r.role as AppRole);
        }
      });
    }
    if (generation !== sessionGeneration.current) return;
    setAllRoles(roles);

    // Recupera a role salva no localStorage ou escolhe a de maior prioridade disponível
    const savedRole = localStorage.getItem(`active_role_${userId}`) as AppRole | null;
    if (savedRole && roles.includes(savedRole)) {
      setRole(savedRole);
    } else {
      const best = ROLE_PRIORITY.find((r) => roles.includes(r)) ?? "buyer";
      setRole(best);
    }
  };

  const switchRole = (newRole: AppRole) => {
    if (allRoles.includes(newRole)) {
      setRole(newRole);
      if (user?.id) {
        localStorage.setItem(`active_role_${user.id}`, newRole);
      }
    }
  };

  useEffect(() => {
    let sessionHandled = false;
    let mounted = true;

    const handleSession = (session: Session | null) => {
      console.info("[auth] handleSession: " + JSON.stringify({
        hasSession: !!session,
        userId: session?.user?.id ?? null,
        expiresAt: session?.expires_at ?? null,
      }));
      const nextUserId = session?.user?.id ?? null;
      const generation = ++sessionGeneration.current;
      if (activeUserId.current !== nextUserId) {
        queryClient.clear();
        setProfile(null);
        setRole(null);
        setAllRoles([]);
      }
      activeUserId.current = nextUserId;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // ensureProfile é fire-and-forget: não bloqueia o carregamento do perfil/role.
        // Para usuários recorrentes o profile já existe — não faz sentido esperar a Edge Function.
        ensureProfile(session.user.id);
        fetchProfile(session.user.id, generation);
        fetchRole(session.user.id, generation);
      } else {
        setProfile(null);
        setRole(null);
        setAllRoles([]);
      }
      setLoading(false);
    };

    // Set up listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        sessionHandled = true;
        if (event === "SIGNED_OUT") {
          clearAppLocalStorage();
          clearAppSessionStorage();
        }
        // Use setTimeout to avoid Supabase deadlock on initial call
        setTimeout(() => { if (mounted) handleSession(session); }, 0);
      }
    );

    // Fallback: check existing session only if onAuthStateChange hasn't fired
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.warn("[auth] Stale session detected, clearing storage:", error.message);
        clearAppLocalStorage();
        clearAppSessionStorage();
        if (mounted && !sessionHandled) {
          handleSession(null);
        }
        return;
      }
      if (mounted && !sessionHandled) {
        handleSession(session);
      }
    }).catch((err) => {
      console.warn("[auth] Exception checking session:", err);
      clearAppLocalStorage();
      clearAppSessionStorage();
      if (mounted && !sessionHandled) {
        handleSession(null);
      }
    });

    return () => {
      mounted = false;
      sessionGeneration.current++;
      subscription.unsubscribe();
    };
  }, []);

  // Sincroniza usuário e papel com o Sentry e Microsoft Clarity
  useEffect(() => {
    if (user) {
      const userData = {
        id: user.id,
        email: user.email,
        role: role ?? undefined,
      };
      setSentryUser(userData);
      setClarityUser(userData);
    } else {
      setSentryUser(null);
      setClarityUser(null);
    }
  }, [user, role]);

  // Auto-redirect based on role after login
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only redirect when we have a logged-in user and a resolved role
    if (!loading && user && role) {
      // If user is a producer and currently on the public landing (or root), send to producer dashboard
      if (role === "producer" && (location.pathname === "/" || location.pathname === "")) {
        navigate("/produtor/dashboard", { replace: true });
      }
      // If user switched away from producer (e.g., logged out or changed role) we don't force navigation here
    }
  }, [loading, user, role, navigate, location.pathname]);

  const signOut = async () => {
    await supabase.auth.signOut();
    sessionGeneration.current++;
    activeUserId.current = null;
    queryClient.clear(); // CRITICAL: Clear React Query cache to prevent cross-account data leaks
    clearAppLocalStorage();
    clearAppSessionStorage();
    setSession(null);
    setUser(null);
    setProfile(null);
    setRole(null);
    setAllRoles([]);
  };

  const refetchRole = async () => {
    const { data: { session: s } } = await supabase.auth.getSession();
    console.info("[auth] refetchRole", {
      hasSession: !!s,
      userId: s?.user?.id ?? null,
      hasAccessToken: !!s?.access_token,
    });
    if (s?.user) {
      await Promise.all([fetchProfile(s.user.id), fetchRole(s.user.id)]);
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, role, allRoles, switchRole, loading, signOut, refetchRole }}>
      {children}
    </AuthContext.Provider>
  );
}
